import {
    ArrowsRightLeftIcon,
    Bars3Icon,
    BeakerIcon,
    CheckIcon,
    DocumentChartBarIcon,
    FunnelIcon,
    PaperAirplaneIcon,
    PencilIcon,
    PlusIcon,
    SparklesIcon,
    Squares2X2Icon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import { VideoCameraIcon } from "@heroicons/react/24/solid";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthStatus } from "../hooks/useAuthStatus";
import {
    fetchAllMatches,
    fetchAllPlayers,
    fetchUserPlaylists,
    mergePlayerRecords,
    saveMatch,
    savePlayer,
} from "../utils/dbutils";
import { enrichPlayerFromWikidata } from "../utils/playerEnricher";
import { uuid } from "../utils/utils";
import { PlaylistViewer, ShareModal } from "./Playlists";

const ROUNDS = [
  { label: "Q1", number: 1 }, { label: "Q2", number: 2 },
  { label: "Q3", number: 3 }, { label: "Q4", number: 4 },
  { label: "R1", number: 5 }, { label: "R2", number: 6 },
  { label: "R3", number: 7 }, { label: "R4", number: 8 },
  { label: "R5", number: 9 },
  { label: "Quarter Final", number: 10 },
  { label: "Semi Final", number: 11 },
  { label: "Final", number: 12 },
];
const DEFAULT_SET_CONFIG = { tiebreakAfterGame: 12, tiebreakTo: 7, tiebreakMargin: 2 };

const DEFAULT_NOTES_CATEGORIES = ["Overview", "Serving", "Playing Style", "Mental Game", "Physicality", "Other"];
const SETTINGS_KEY = "psts_notes_categories";
const SELECTED_PLAYER_KEY = "psts_players_selected_guid";

// Split a full name (e.g. Wikidata label) into first/last: the last whitespace
// token is the surname, everything before it the given name(s).
function splitName(full) {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
}

function loadNotesCategories() {
  return JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "null") ?? DEFAULT_NOTES_CATEGORIES;
}
const MIN_PANEL = 160;
const MAX_PANEL = 480;
const DEFAULT_PANEL = 240;
const HAND_LABELS = { 0: "Right", 1: "Left", 2: "Unknown" };

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-1 flex-wrap">
      <span className="text-xs text-base-content/50 shrink-0">{label}:</span>
      <span className="text-xs font-medium">{value}</span>
    </div>
  );
}

function LabeledField({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-base-content/50">{label}</span>
      {children}
    </div>
  );
}

function Players() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;

  // Left panel resize
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(DEFAULT_PANEL);

  // Info panel resize (upper-right split)
  const [infoPanelWidth, setInfoPanelWidth] = useState(320);
  const infoDragging = useRef(false);
  const infoStartX = useRef(0);
  const infoStartWidth = useRef(320);

  // Data
  const [players, setPlayers] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [allPlaylists, setAllPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection & search
  const [selectedGuid, setSelectedGuid] = useState(
    () => localStorage.getItem(SELECTED_PLAYER_KEY) || null
  );
  const [search, setSearch] = useState("");

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [merging, setMerging] = useState(false);
  const [showNewPlayerModal, setShowNewPlayerModal] = useState(false);
  const [sharePlaylistTarget, setSharePlaylistTarget] = useState(null);
  const [viewingPlaylist, setViewingPlaylist] = useState(null);
  const [showNewMatchModal, setShowNewMatchModal] = useState(false);

  // Mobile: show the player list or the details one at a time.
  const [isMobile, setIsMobile] = useState(false);
  const [showList, setShowList] = useState(true);
  // Mobile portrait: stack player details, matches and playlist vertically.
  const [portraitStack, setPortraitStack] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px) and (orientation: portrait)");
    const update = () => setPortraitStack(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Notes — categorised { [category]: [{text, ts}] }
  const [categoryNotes, setCategoryNotes] = useState({});
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState({});
  const [notesCategories] = useState(loadNotesCategories);

  // ── Data loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    Promise.all([fetchAllPlayers(uid), fetchAllMatches(uid)]).then(([ps, ms]) => {
      const sorted = (ps ?? []).sort((a, b) =>
        `${a.lastName ?? ""}${a.firstName ?? ""}`.localeCompare(
          `${b.lastName ?? ""}${b.firstName ?? ""}`
        )
      );
      setPlayers(sorted);
      setAllMatches(ms ?? []);
      setLoading(false);
      // Clear stale persisted selection if that player no longer exists.
      setSelectedGuid((prev) =>
        prev && sorted.some((p) => p.guid === prev) ? prev : null
      );
    });
  }, [uid]);

  // Persist the selected player so it's restored on next visit.
  useEffect(() => {
    if (selectedGuid) localStorage.setItem(SELECTED_PLAYER_KEY, selectedGuid);
    else localStorage.removeItem(SELECTED_PLAYER_KEY);
  }, [selectedGuid]);

  useEffect(() => {
    if (!uid) return;
    fetchUserPlaylists(uid).then(setAllPlaylists);
  }, [uid]);

  // ── Drag handle ───────────────────────────────────────────────────────────────
  const onMouseDown = useCallback(
    (e) => {
      dragging.current = true;
      startX.current = e.clientX;
      startWidth.current = panelWidth;
      e.preventDefault();
    },
    [panelWidth]
  );

  useEffect(() => {
    const onMove = (e) => {
      if (dragging.current) {
        setPanelWidth(
          Math.min(MAX_PANEL, Math.max(MIN_PANEL, startWidth.current + (e.clientX - startX.current)))
        );
      }
      if (infoDragging.current) {
        setInfoPanelWidth(
          Math.min(800, Math.max(180, infoStartWidth.current + (e.clientX - infoStartX.current)))
        );
      }
    };
    const onUp = () => {
      dragging.current = false;
      infoDragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // ── Selection ─────────────────────────────────────────────────────────────────
  const selectedPlayer = players.find((p) => p.guid === selectedGuid) ?? null;

  useEffect(() => {
    // Load notes — support old plain-string format
    const raw = selectedPlayer?.notes ?? null;
    let parsed = {};
    if (raw && typeof raw === "object") {
      parsed = raw;
    } else if (typeof raw === "string" && raw.trim()) {
      parsed = { Overview: [{ text: raw, ts: Date.now() }] };
    }
    setCategoryNotes(parsed);
    setNotesEditing(false);
    setNotesDraft({});
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGuid]);

  const filteredPlayers = players.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.firstName?.toLowerCase().includes(s) || p.lastName?.toLowerCase().includes(s);
  });

  // Match count per player guid (for display in the list)
  const matchCountByGuid = {};
  for (const m of allMatches) {
    if (m.player1Guid) matchCountByGuid[m.player1Guid] = (matchCountByGuid[m.player1Guid] ?? 0) + 1;
    if (m.player2Guid) matchCountByGuid[m.player2Guid] = (matchCountByGuid[m.player2Guid] ?? 0) + 1;
  }

  // Names that appear more than once → flag as potential duplicates
  const nameCounts = {};
  for (const p of players) {
    const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim().toLowerCase();
    if (name) nameCounts[name] = (nameCounts[name] ?? 0) + 1;
  }
  const duplicateNames = new Set(Object.keys(nameCounts).filter((n) => nameCounts[n] > 1));

  const playerMatches = allMatches
    .filter(
      (m) =>
        m.player1Guid === selectedGuid ||
        m.player2Guid === selectedGuid ||
        m.player1?.guid === selectedGuid ||
        m.player2?.guid === selectedGuid
    )
    .sort((a, b) => (b.matchDate ?? 0) - (a.matchDate ?? 0));

  const existingTournaments = [...new Set(allMatches.map((m) => m.tournament?.name).filter(Boolean))].sort();

  const playerPlaylists = allPlaylists.filter(
    (p) => p.playerGuid === selectedGuid || p.data?.playerGuid === selectedGuid
  );

  // ── Edit handlers ─────────────────────────────────────────────────────────────
  const startEditing = () => {
    if (!selectedPlayer) return;
    setEditFields({
      firstName: selectedPlayer.firstName ?? "",
      lastName: selectedPlayer.lastName ?? "",
      dob: selectedPlayer.dob ? new Date(selectedPlayer.dob).toISOString().slice(0, 10) : "",
      height: selectedPlayer.height ? String(selectedPlayer.height) : "",
      hand: String(selectedPlayer.hand ?? 0),
      gender: selectedPlayer.gender ?? "",
      atpId: selectedPlayer.atpId ?? "",
      wtaId: selectedPlayer.wtaId ?? "",
      countryName: selectedPlayer.country?.name ?? "",
      countryCode: selectedPlayer.country?.code ?? "",
      photoUrl: selectedPlayer.photoUrl ?? "",
      wikidataId: selectedPlayer.wikidataId ?? "",
    });
    setEditing(true);
  };

  const cancelEditing = () => setEditing(false);

  const handleMergeInto = async (targetGuid) => {
    if (!selectedGuid || !targetGuid) return;
    setMerging(true);
    const result = await mergePlayerRecords(uid, selectedGuid, targetGuid);
    setMerging(false);
    if (result.error) {
      toast.error("Merge failed: " + result.error);
      return;
    }
    const target = players.find((p) => p.guid === targetGuid);
    toast.success(
      `Merged into ${target?.firstName ?? ""} ${target?.lastName ?? ""} (${result.updatedCount} match(es) updated)`
    );
    setShowMergeModal(false);
    setLoading(true);
    const [ps, ms] = await Promise.all([fetchAllPlayers(uid), fetchAllMatches(uid)]);
    setPlayers(
      (ps ?? []).sort((a, b) =>
        `${a.lastName ?? ""}${a.firstName ?? ""}`.localeCompare(
          `${b.lastName ?? ""}${b.firstName ?? ""}`
        )
      )
    );
    setAllMatches(ms ?? []);
    setLoading(false);
    setSelectedGuid(targetGuid);
  };

  const saveEditing = async () => {
    if (!selectedPlayer) return;
    setSaving(true);
    const updated = {
      ...selectedPlayer,
      firstName: editFields.firstName,
      lastName: editFields.lastName,
      dob: editFields.dob ? new Date(editFields.dob).getTime() : 0,
      height: editFields.height !== "" ? Number(editFields.height) : 0,
      hand: Number(editFields.hand),
      gender: editFields.gender,
      atpId: editFields.atpId,
      wtaId: editFields.wtaId,
      country: { name: editFields.countryName, code: editFields.countryCode },
      notes: categoryNotes,
    };
    if (editFields.photoUrl) updated.photoUrl = editFields.photoUrl;
    if (editFields.wikidataId) updated.wikidataId = editFields.wikidataId;
    await savePlayer(updated);
    setPlayers((prev) =>
      prev
        .map((p) => (p.guid === updated.guid ? updated : p))
        .sort((a, b) =>
          `${a.lastName ?? ""}${a.firstName ?? ""}`.localeCompare(
            `${b.lastName ?? ""}${b.firstName ?? ""}`
          )
        )
    );
    setSaving(false);
    setEditing(false);
  };

  const saveCategoryNotes = async (updated) => {
    if (!selectedPlayer) return;
    const updatedPlayer = { ...selectedPlayer, notes: updated };
    await savePlayer(updatedPlayer);
    setPlayers((prev) => prev.map((p) => (p.guid === updatedPlayer.guid ? updatedPlayer : p)));
  };

  const enterNotesEdit = () => {
    const draft = {};
    notesCategories.forEach((cat) => {
      draft[cat] = (categoryNotes[cat] ?? []).map((c) => c.text).join("\n");
    });
    setNotesDraft(draft);
    setNotesEditing(true);
  };

  const saveNotesEdit = async () => {
    const updated = {};
    notesCategories.forEach((cat) => {
      const lines = (notesDraft[cat] ?? "").split("\n").map((s) => s.trim()).filter(Boolean);
      const existing = categoryNotes[cat] ?? [];
      updated[cat] = lines.map((text) => existing.find((c) => c.text === text) ?? { text, ts: Date.now() });
    });
    setCategoryNotes(updated);
    setNotesEditing(false);
    setNotesDraft({});
    await saveCategoryNotes(updated);
  };

  const cancelNotesEdit = () => {
    setNotesEditing(false);
    setNotesDraft({});
  };

  const setField = (k, v) => setEditFields((prev) => ({ ...prev, [k]: v }));

  // Look the current player up on Wikidata and fill in any fields it provides.
  const [enriching, setEnriching] = useState(false);
  const fetchFromWikidata = async () => {
    const firstName = (editFields.firstName ?? "").trim();
    const lastName = (editFields.lastName ?? "").trim();
    if (!firstName && !lastName) {
      toast.info("Enter a name first");
      return;
    }
    setEnriching(true);
    try {
      const data = await enrichPlayerFromWikidata(firstName, lastName);
      if (!data) {
        toast.warn(`No Wikidata match for "${`${firstName} ${lastName}`.trim()}"`);
        return;
      }
      setEditFields((prev) => {
        const next = { ...prev };
        // Fill in a missing first/last name from Wikidata's canonical label.
        if (data.name) {
          const { first, last } = splitName(data.name);
          if (!next.firstName?.trim() && first) next.firstName = first;
          if (!next.lastName?.trim() && last) next.lastName = last;
        }
        if (data.dob) next.dob = new Date(data.dob).toISOString().slice(0, 10);
        if (data.height) next.height = String(data.height);
        if (data.hand !== undefined) next.hand = String(data.hand);
        if (data.gender) next.gender = data.gender;
        if (data.atpId) next.atpId = data.atpId;
        if (data.wtaId) next.wtaId = data.wtaId;
        if (data.country?.name) next.countryName = data.country.name;
        if (data.country?.code) next.countryCode = data.country.code;
        if (data.photoUrl) next.photoUrl = data.photoUrl;
        if (data.wikidataId) next.wikidataId = data.wikidataId;
        return next;
      });
      toast.success("Filled from Wikidata");
    } catch (err) {
      toast.error("Wikidata lookup failed");
    } finally {
      setEnriching(false);
    }
  };

  const createNewPlayer = async (fields) => {
    const guid = uuid();
    const player = {
      uid,
      guid,
      firstName: fields.firstName.trim(),
      lastName: fields.lastName.trim(),
      hand: Number(fields.hand),
      gender: fields.gender,
      dob: fields.dob ?? 0,
      height: fields.height ?? 0,
      atpId: fields.atpId ?? "",
      wtaId: fields.wtaId ?? "",
      country: fields.country ?? { name: "", code: "" },
      notes: {},
    };
    if (fields.photoUrl) player.photoUrl = fields.photoUrl;
    if (fields.wikidataId) player.wikidataId = fields.wikidataId;
    await savePlayer(player);
    setPlayers((prev) =>
      [...prev, player].sort((a, b) =>
        `${a.lastName ?? ""}${a.firstName ?? ""}`.localeCompare(
          `${b.lastName ?? ""}${b.firstName ?? ""}`
        )
      )
    );
    setSelectedGuid(guid);
    setShowNewPlayerModal(false);
  };

  // Selection
  const [selectedMatchGuids, setSelectedMatchGuids] = useState(new Set());

  // Clear selection when player changes
  useEffect(() => { setSelectedMatchGuids(new Set()); }, [selectedGuid]);

  // A match is selectable only if it shares the focus player (always true here
  // since playerMatches already filters by player)
  const toggleMatchSelect = (guid) => {
    setSelectedMatchGuids((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  };

  const handleCreateMatch = async (match) => {
    const result = await saveMatch(match, uid);
    if (result.error) {
      toast.error("Failed to create match: " + result.error);
      return;
    }
    toast.success("Match created");
    setShowNewMatchModal(false);
    // reload matches so it appears in the list
    const ms = await fetchAllMatches(uid);
    setAllMatches(ms ?? []);
    // navigate to coding page
    match.sets = match.sets ?? [];
    const st = {
      player1: match.player1,
      player2: match.player2,
      match,
    };
    navigate("/match-coding", { state: st });
  };

  const toggleSelectAllMatches = () => {
    if (selectedMatchGuids.size === playerMatches.length) {
      setSelectedMatchGuids(new Set());
    } else {
      setSelectedMatchGuids(new Set(playerMatches.map((m) => m.guid)));
    }
  };

  const opponentName = (m) => {
    const isP1 = m.player1Guid === selectedGuid || m.player1?.guid === selectedGuid;
    if (isP1) {
      const fn = m.player2FirstName ?? m.player2?.firstName ?? "";
      const ln = m.player2LastName ?? m.player2?.lastName ?? "";
      return `${fn} ${ln}`.trim() || "?";
    }
    const fn = m.player1FirstName ?? m.player1?.firstName ?? "";
    const ln = m.player1LastName ?? m.player1?.lastName ?? "";
    return `${fn} ${ln}`.trim() || "?";
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (viewingPlaylist) {
    return (
      <PlaylistViewer
        playlist={viewingPlaylist}
        onBack={() => setViewingPlaylist(null)}
      />
    );
  }

  return (
    <div className="flex h-[calc(100vh-3rem)] overflow-hidden">

      {/* ── Left panel: player list ──────────────────────────────────────── */}
      {(!isMobile || showList) && (
      <div
        className="flex flex-col shrink-0 border-r border-base-300"
        style={{ width: isMobile ? "100%" : panelWidth }}
      >
        <div className="px-2 py-2 border-b border-base-300 bg-base-200 shrink-0 flex gap-1">
          <input
            type="text"
            className="input input-bordered input-sm flex-1 min-w-0"
            placeholder="Search players…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className="btn btn-ghost btn-sm btn-square shrink-0"
            title="New player"
            onClick={() => setShowNewPlayerModal(true)}
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <span className="loading loading-spinner loading-md" />
          </div>
        ) : (
          <div className="overflow-y-auto flex-1">
            {filteredPlayers.length === 0 && (
              <p className="text-xs text-base-content/50 px-3 py-4 text-center">
                {players.length === 0 ? "No players yet." : "No matches."}
              </p>
            )}
            {filteredPlayers.map((p) => {
              const matchCount = matchCountByGuid[p.guid] ?? 0;
              const fullName = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim();
              const isDuplicate = duplicateNames.has(fullName.toLowerCase());
              return (
              <button
                key={p.guid}
                className={`w-full text-left px-3 py-2 text-sm border-b border-base-300/40 flex items-center gap-2 hover:bg-base-200${
                  selectedGuid === p.guid ? " bg-primary/10 font-semibold" : ""
                }`}
                onClick={() => { setSelectedGuid(p.guid); if (isMobile) setShowList(false); }}
              >
                {p.photoUrl ? (
                  <img
                    src={p.photoUrl}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-base-300 flex items-center justify-center shrink-0 text-xs font-bold text-base-content/50">
                    {(p.firstName?.[0] ?? p.lastName?.[0] ?? "?").toUpperCase()}
                  </div>
                )}
                <span className="truncate flex-1">
                  {fullName || "Unknown"}
                </span>
                <span className="shrink-0 flex items-center gap-1">
                  {isDuplicate && (
                    <span className="badge badge-warning badge-xs" title="Duplicate name — consider merging">!</span>
                  )}
                  <span className="text-xs text-base-content/40">{matchCount}</span>
                </span>
              </button>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Drag handle */}
      {!isMobile && (
      <div
        className="w-1 cursor-col-resize bg-base-300 hover:bg-primary active:bg-primary transition-colors shrink-0"
        onMouseDown={onMouseDown}
      />
      )}

      {/* ── Right panel ──────────────────────────────────────────────────── */}
      {(!isMobile || !showList) && (
      <div className={`flex flex-col flex-1 ${portraitStack ? "overflow-y-auto" : "overflow-hidden"}`}>
        {isMobile && (
          <button
            className="btn btn-ghost btn-sm gap-1 self-start m-1 shrink-0"
            onClick={() => setShowList(true)}
            title="Back to players list"
          >
            <Bars3Icon className="w-4 h-4" />
            Players
          </button>
        )}
        {!selectedPlayer ? (
          <div className="flex items-center justify-center flex-1 text-base-content/40 text-sm">
            Select a player from the list
          </div>
        ) : (
          <>
            {/* ── Upper panel (Player details) ── */}
            <div
              className={`shrink-0 border-b border-base-300 ${portraitStack ? "flex flex-col" : "flex overflow-hidden"}`}
              style={portraitStack ? undefined : { height: "42%", minHeight: "200px", maxHeight: "380px" }}
            >
              {/* Player info (left half — resizable) */}
              <div
                className={`p-4 shrink-0 ${portraitStack ? "w-full" : "overflow-y-auto"}`}
                style={portraitStack ? undefined : { width: infoPanelWidth }}
              >
                {/* Header row: photo · name · action buttons */}
                <div className="flex items-start gap-3 mb-3">
                  {/* Photo */}
                  <div className="shrink-0">
                    {selectedPlayer.photoUrl ? (
                      <img
                        src={selectedPlayer.photoUrl}
                        alt="Player"
                        className="w-20 h-20 rounded-box object-cover border border-base-300"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-box bg-base-300 flex items-center justify-center text-3xl font-bold text-base-content/30">
                        {(
                          selectedPlayer.firstName?.[0] ??
                          selectedPlayer.lastName?.[0] ??
                          "?"
                        ).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    {editing ? (
                      <div className="flex gap-1 flex-wrap">
                        <input
                          className="input input-bordered input-sm w-28"
                          value={editFields.firstName}
                          placeholder="First name"
                          onChange={(e) => setField("firstName", e.target.value)}
                        />
                        <input
                          className="input input-bordered input-sm w-28"
                          value={editFields.lastName}
                          placeholder="Last name"
                          onChange={(e) => setField("lastName", e.target.value)}
                        />
                      </div>
                    ) : (
                      <span className="text-lg font-bold">
                        {`${selectedPlayer.firstName ?? ""} ${selectedPlayer.lastName ?? ""}`.trim() ||
                          "Unknown"}
                      </span>
                    )}
                  </div>

                  {/* Action buttons — top right */}
                  <div className="flex gap-1 shrink-0">
                    {editing ? (
                      <>
                        <button
                          className="btn btn-xs btn-ghost text-info"
                          onClick={fetchFromWikidata}
                          disabled={enriching}
                          title="Fill details from Wikidata"
                        >
                          {enriching ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            <SparklesIcon className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          className="btn btn-xs btn-success"
                          onClick={saveEditing}
                          disabled={saving}
                        >
                          {saving ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            <CheckIcon className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button className="btn btn-xs btn-ghost" onClick={cancelEditing}>
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-xs btn-ghost" onClick={startEditing} title="Edit player">
                          <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          className="btn btn-xs btn-ghost text-warning"
                          onClick={() => setShowMergeModal(true)}
                          title="Merge this player into another"
                        >
                          <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Detail fields — full width, utilises space under photo */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {editing ? (
                    <>
                      <LabeledField label="Date of Birth">
                        <input
                          type="date"
                          className="input input-bordered input-xs w-full"
                          value={editFields.dob}
                          onChange={(e) => setField("dob", e.target.value)}
                        />
                      </LabeledField>
                      <LabeledField label="Height (cm)">
                        <input
                          type="number"
                          className="input input-bordered input-xs w-full"
                          value={editFields.height}
                          onChange={(e) => setField("height", e.target.value)}
                        />
                      </LabeledField>
                      <LabeledField label="Handedness">
                        <select
                          className="select select-bordered select-xs w-full"
                          value={editFields.hand}
                          onChange={(e) => setField("hand", e.target.value)}
                        >
                          <option value="0">Right</option>
                          <option value="1">Left</option>
                          <option value="2">Unknown</option>
                        </select>
                      </LabeledField>
                      <LabeledField label="Gender">
                        <select
                          className="select select-bordered select-xs w-full"
                          value={editFields.gender}
                          onChange={(e) => setField("gender", e.target.value)}
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Unknown">Unknown</option>
                        </select>
                      </LabeledField>
                      <LabeledField label="ATP ID">
                        <input
                          className="input input-bordered input-xs w-full"
                          value={editFields.atpId}
                          onChange={(e) => setField("atpId", e.target.value)}
                        />
                      </LabeledField>
                      <LabeledField label="WTA ID">
                        <input
                          className="input input-bordered input-xs w-full"
                          value={editFields.wtaId}
                          onChange={(e) => setField("wtaId", e.target.value)}
                        />
                      </LabeledField>
                      <LabeledField label="Country">
                        <input
                          className="input input-bordered input-xs w-full"
                          value={editFields.countryName}
                          onChange={(e) => setField("countryName", e.target.value)}
                        />
                      </LabeledField>
                      <LabeledField label="Country Code">
                        <input
                          className="input input-bordered input-xs w-20"
                          value={editFields.countryCode}
                          onChange={(e) => setField("countryCode", e.target.value)}
                        />
                      </LabeledField>
                    </>
                  ) : (
                    <>
                      <InfoRow
                        label="DOB"
                        value={
                          selectedPlayer.dob
                            ? new Date(selectedPlayer.dob).toLocaleDateString()
                            : "—"
                        }
                      />
                      <InfoRow
                        label="Height"
                        value={selectedPlayer.height ? `${selectedPlayer.height} cm` : "—"}
                      />
                      <InfoRow
                        label="Hand"
                        value={HAND_LABELS[selectedPlayer.hand ?? 0] ?? "—"}
                      />
                      <InfoRow label="Gender" value={selectedPlayer.gender || "—"} />
                      <InfoRow label="ATP ID" value={selectedPlayer.atpId || "—"} />
                      <InfoRow label="WTA ID" value={selectedPlayer.wtaId || "—"} />
                      <InfoRow
                        label="Country"
                        value={selectedPlayer.country?.name || "—"}
                      />
                      {selectedPlayer.country?.code && (
                        <InfoRow label="Code" value={selectedPlayer.country.code} />
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Info/Notes drag handle */}
              {!portraitStack && (
              <div
                className="w-1 cursor-col-resize bg-base-300 hover:bg-primary active:bg-primary transition-colors shrink-0"
                onMouseDown={(e) => {
                  infoDragging.current = true;
                  infoStartX.current = e.clientX;
                  infoStartWidth.current = infoPanelWidth;
                  e.preventDefault();
                }}
              />
              )}

              {/* Notes panel — categorised */}
              <div
                className={`flex flex-col ${portraitStack ? "w-full border-t border-base-300" : "flex-1 overflow-hidden"}`}
                style={portraitStack ? undefined : { minWidth: "180px" }}
              >
                {/* Notes header */}
                <div className="flex items-center gap-1 px-3 py-2 border-b border-base-300 shrink-0">
                  <span className="text-xs font-semibold uppercase tracking-wide text-base-content/50 flex-1">
                    Notes
                  </span>
                  {!notesEditing && selectedPlayer && (
                    <button className="btn btn-ghost btn-xs" onClick={enterNotesEdit} title="Edit notes">
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {notesEditing && (
                    <>
                      <button className="btn btn-ghost btn-xs text-success" onClick={saveNotesEdit} title="Save">
                        <CheckIcon className="w-3.5 h-3.5" />
                      </button>
                      <button className="btn btn-ghost btn-xs text-error" onClick={cancelNotesEdit} title="Cancel">
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>

                {/* Notes body */}
                <div className={`px-3 py-3 flex flex-col gap-4 ${portraitStack ? "" : "overflow-y-auto flex-1"}`}>
                  {notesCategories.map((cat) => (
                    <div key={cat}>
                      <div className="text-xs font-bold uppercase tracking-wide text-primary mb-1">
                        {cat}
                      </div>
                      {notesEditing ? (
                        <textarea
                          className="textarea textarea-bordered textarea-xs w-full resize-y text-sm leading-relaxed"
                          rows={3}
                          placeholder={`One note per line…`}
                          value={notesDraft[cat] ?? ""}
                          onChange={(e) =>
                            setNotesDraft((prev) => ({ ...prev, [cat]: e.target.value }))
                          }
                        />
                      ) : (
                        <div className="flex flex-col gap-0.5 pl-0.5">
                          {(categoryNotes[cat] ?? []).length === 0 ? (
                            <span className="text-xs text-base-content/30 italic">—</span>
                          ) : (
                            (categoryNotes[cat] ?? []).map((c, i) => (
                              <p key={i} className="text-xs leading-relaxed text-base-content">
                                {c.text}
                              </p>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Lower panel ── */}
            <div className={portraitStack ? "flex flex-col shrink-0" : "flex flex-1 overflow-hidden"}>
              {/* Matches */}
              <div className={`flex flex-col ${portraitStack ? "w-full border-b border-base-300" : "flex-1 overflow-hidden border-r border-base-300"}`}>
                <div className="px-3 py-2 border-b border-base-300 bg-base-200 shrink-0 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-base-content/60">
                    Matches ({playerMatches.length}){selectedMatchGuids.size > 0 && (
                      <span className="ml-2 badge badge-primary badge-sm">{selectedMatchGuids.size} selected</span>
                    )}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <button
                      className="btn btn-ghost btn-xs"
                      title="New match with this player as Player 1"
                      disabled={!selectedPlayer}
                      onClick={() => setShowNewMatchModal(true)}
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                    </button>
                    {/* <button
                      className="btn btn-ghost btn-xs"
                      title={`Match stats (${selectedMatchGuids.size})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/stats", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <ChartBarIcon className="w-3.5 h-3.5" />
                    </button> */}
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`General report (${selectedMatchGuids.size})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/report", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <DocumentChartBarIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`Filters analysis (${selectedMatchGuids.size})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/filters-analysis", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <FunnelIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`Shots analysis (${selectedMatchGuids.size})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/shots-analysis", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <Squares2X2Icon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`AI Report (${selectedMatchGuids.size} match${selectedMatchGuids.size !== 1 ? "es" : ""})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/ai-report", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <SparklesIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`AI Report by Events (${selectedMatchGuids.size} match${selectedMatchGuids.size !== 1 ? "es" : ""})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/ai-report-events", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <BeakerIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className={portraitStack ? "" : "overflow-y-auto flex-1"}>
                  {playerMatches.length === 0 ? (
                    <p className="text-xs text-base-content/50 px-4 py-6 text-center">
                      No matches found for this player.
                    </p>
                  ) : (
                    <div className="p-2 space-y-2">
                      <label className="flex items-center gap-2 px-1 text-xs text-base-content/60 cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs"
                          checked={playerMatches.length > 0 && selectedMatchGuids.size === playerMatches.length}
                          onChange={toggleSelectAllMatches}
                        />
                        Select all
                      </label>
                      {playerMatches.map((m) => (
                        <div
                          key={m.guid ?? m.firestoreId}
                          className={`rounded-box border p-3 ${
                            selectedMatchGuids.has(m.guid)
                              ? "border-primary bg-base-200"
                              : "border-base-300 bg-base-100"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm mt-0.5 shrink-0"
                              checked={selectedMatchGuids.has(m.guid)}
                              onChange={() => toggleMatchSelect(m.guid)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm truncate">
                                {opponentName(m)}
                              </div>
                              <div className="text-xs text-base-content/60 mt-0.5 truncate">
                                {m.matchDate ? new Date(m.matchDate).toLocaleDateString() : "—"}
                                {m.tournament?.name ? ` · ${m.tournament.name}` : ""}
                              </div>
                            </div>
                            <button
                              className="btn btn-ghost btn-xs btn-square shrink-0"
                              title="Video analysis"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/video-analysis", { state: { matchGuids: [m.guid], playerGuid: selectedGuid } });
                              }}
                            >
                              <VideoCameraIcon className={`w-4 h-4 ${m.videoFile ? "text-success" : ""}`} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Playlists */}
              <div
                className={`flex flex-col ${portraitStack ? "w-full border-t border-base-300" : "overflow-hidden shrink-0"}`}
                style={portraitStack ? undefined : { width: "32%", minWidth: "180px" }}
              >
                <div className="px-3 py-2 border-b border-base-300 bg-base-200 shrink-0 text-xs font-semibold uppercase tracking-wide text-base-content/60">
                  Playlists ({playerPlaylists.length})
                </div>
                <div className={portraitStack ? "" : "overflow-y-auto flex-1"}>
                  {playerPlaylists.length === 0 ? (
                    <p className="text-xs text-base-content/50 px-4 py-6 text-center">
                      No playlists for this player.
                    </p>
                  ) : (
                    playerPlaylists.map((pl) => (
                      <div
                        key={pl.firestoreId}
                        className="flex items-center gap-1 px-2 py-2 border-b border-base-300/40 hover:bg-base-200"
                      >
                        <button
                          className="flex-1 text-left min-w-0"
                          onClick={() => setViewingPlaylist(pl)}
                        >
                          <div className="text-sm font-medium truncate">{pl.name}</div>
                          <div className="text-xs text-base-content/50 mt-0.5">
                            {pl.updatedAt
                              ? new Date(pl.updatedAt).toLocaleDateString()
                              : ""}
                            {pl.items != null ? ` · ${pl.items} clips` : ""}
                          </div>
                        </button>
                        <button
                          className="btn btn-ghost btn-xs btn-square tooltip tooltip-left"
                          data-tip="Share"
                          disabled={!uid}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSharePlaylistTarget(pl);
                          }}
                        >
                          <PaperAirplaneIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="btn btn-ghost btn-xs btn-square tooltip tooltip-left"
                          data-tip="Open playlist"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingPlaylist(pl);
                          }}
                        >
                          <VideoCameraIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {showMergeModal && selectedPlayer && (
        <MergeIntoModal
          fromPlayer={selectedPlayer}
          players={players.filter((p) => p.guid !== selectedGuid)}
          merging={merging}
          onClose={() => setShowMergeModal(false)}
          onConfirm={handleMergeInto}
        />
      )}

      {sharePlaylistTarget && (
        <ShareModal
          playlists={[sharePlaylistTarget]}
          senderUid={uid}
          senderEmail={firebaseUser?.email ?? ""}
          senderName={firebaseUser?.displayName ?? firebaseUser?.email ?? ""}
          onClose={() => setSharePlaylistTarget(null)}
        />
      )}

      {showNewPlayerModal && (
        <NewPlayerModal
          onClose={() => setShowNewPlayerModal(false)}
          onCreate={createNewPlayer}
        />
      )}

      {showNewMatchModal && selectedPlayer && (
        <NewMatchFromPlayerModal
          uid={uid}
          player1={selectedPlayer}
          players={players.filter((p) => p.guid !== selectedGuid)}
          existingTournaments={existingTournaments}
          onSave={handleCreateMatch}
          onClose={() => setShowNewMatchModal(false)}
        />
      )}
    </div>
  );
}

// ── NewPlayerModal ──────────────────────────────────────────────────────────

function NewPlayerModal({ onClose, onCreate }) {
  const [fields, setFields] = useState({ firstName: "", lastName: "", hand: "0", gender: "Male" });
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const set = (k, v) => setFields((prev) => ({ ...prev, [k]: v }));

  const lookupWikidata = async () => {
    const firstName = (fields.firstName ?? "").trim();
    const lastName = (fields.lastName ?? "").trim();
    if (!firstName && !lastName) {
      toast.info("Enter a name first");
      return;
    }
    setEnriching(true);
    try {
      const data = await enrichPlayerFromWikidata(firstName, lastName);
      if (!data) {
        toast.warn(`No Wikidata match for "${`${firstName} ${lastName}`.trim()}"`);
        return;
      }
      setFields((prev) => {
        const next = { ...prev };
        if (data.name) {
          const { first, last } = splitName(data.name);
          if (!next.firstName?.trim() && first) next.firstName = first;
          if (!next.lastName?.trim() && last) next.lastName = last;
        }
        if (data.dob) next.dob = data.dob;
        if (data.height) next.height = data.height;
        if (data.hand !== undefined) next.hand = String(data.hand);
        if (data.gender) next.gender = data.gender;
        if (data.atpId) next.atpId = data.atpId;
        if (data.wtaId) next.wtaId = data.wtaId;
        if (data.country) next.country = data.country;
        if (data.photoUrl) next.photoUrl = data.photoUrl;
        if (data.wikidataId) next.wikidataId = data.wikidataId;
        return next;
      });
      toast.success("Filled from Wikidata");
    } catch (err) {
      toast.error("Wikidata lookup failed");
    } finally {
      setEnriching(false);
    }
  };

  const handleCreate = async () => {
    if (!fields.firstName.trim() || !fields.lastName.trim()) return;
    setSaving(true);
    await onCreate(fields);
    setSaving(false);
  };

  return (
    <div className="modal modal-open z-50">
      <div className="modal-box w-full max-w-sm flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">New Player</h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose} disabled={saving}>
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <div className="flex flex-col gap-0.5 flex-1">
              <span className="text-xs text-base-content/50">First Name</span>
              <input
                className="input input-bordered input-sm w-full"
                value={fields.firstName}
                placeholder="First name"
                autoFocus
                onChange={(e) => set("firstName", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
            </div>
            <div className="flex flex-col gap-0.5 flex-1">
              <span className="text-xs text-base-content/50">Last Name</span>
              <input
                className="input input-bordered input-sm w-full"
                value={fields.lastName}
                placeholder="Last name"
                onChange={(e) => set("lastName", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-outline btn-info btn-sm gap-1"
              onClick={lookupWikidata}
              disabled={enriching}
              title="Fetch date of birth, height, country, IDs and photo from Wikidata"
            >
              {enriching ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <SparklesIcon className="w-4 h-4" />
              )}
              Look up on Wikidata
            </button>
            {(fields.photoUrl || fields.country?.name) && (
              <div className="flex items-center gap-2 text-xs text-base-content/70">
                {fields.photoUrl && (
                  <img
                    src={fields.photoUrl}
                    alt=""
                    className="w-8 h-8 rounded object-cover border border-base-300"
                  />
                )}
                {fields.country?.name && <span>{fields.country.name}</span>}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <div className="flex flex-col gap-0.5 flex-1">
              <span className="text-xs text-base-content/50">Handedness</span>
              <select
                className="select select-bordered select-sm w-full"
                value={fields.hand}
                onChange={(e) => set("hand", e.target.value)}
              >
                <option value="0">Right</option>
                <option value="1">Left</option>
                <option value="2">Unknown</option>
              </select>
            </div>
            <div className="flex flex-col gap-0.5 flex-1">
              <span className="text-xs text-base-content/50">Gender</span>
              <select
                className="select select-bordered select-sm w-full"
                value={fields.gender}
                onChange={(e) => set("gender", e.target.value)}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Unknown">Unknown</option>
              </select>
            </div>
          </div>
        </div>

        <div className="modal-action mt-0">
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            disabled={!fields.firstName.trim() || !fields.lastName.trim() || saving}
            onClick={handleCreate}
          >
            {saving ? <span className="loading loading-spinner loading-xs" /> : "Create"}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={() => !saving && onClose()} />
    </div>
  );
}

// ── MergeIntoModal ─────────────────────────────────────────────────────────

function MergeIntoModal({ fromPlayer, players, merging, onClose, onConfirm }) {
  const [search, setSearch] = useState("");
  const [targetGuid, setTargetGuid] = useState(null);

  const filtered = players.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.firstName?.toLowerCase().includes(s) || p.lastName?.toLowerCase().includes(s);
  });

  const target = players.find((p) => p.guid === targetGuid) ?? null;
  const fromName = `${fromPlayer.firstName ?? ""} ${fromPlayer.lastName ?? ""}`.trim() || "Unknown";
  const toName = target ? `${target.firstName ?? ""} ${target.lastName ?? ""}`.trim() || "Unknown" : "";

  return (
    <div className="modal modal-open z-50">
      <div className="modal-box w-full max-w-sm flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">
            Merge <span className="text-primary">{fromName}</span> into…
          </h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose} disabled={merging}>
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-base-content/60">
          All matches for <strong>{fromName}</strong> will be reassigned to the selected player.
          <strong> {fromName}</strong> will then be deleted.
        </p>

        <input
          type="text"
          className="input input-bordered input-sm w-full"
          placeholder="Search players…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        <div className="border border-base-300 rounded-box overflow-y-auto max-h-52 flex flex-col">
          {filtered.length === 0 ? (
            <p className="text-xs text-base-content/40 px-3 py-4 text-center">No players found.</p>
          ) : (
            filtered.map((p) => {
              const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || "Unknown";
              const isSelected = p.guid === targetGuid;
              return (
                <button
                  key={p.guid}
                  className={`text-left px-3 py-2 text-sm border-b border-base-300/40 last:border-0 flex items-center gap-2 hover:bg-base-200 transition-colors${isSelected ? " bg-primary/10 font-semibold" : ""}`}
                  onClick={() => setTargetGuid(p.guid)}
                >
                  {p.photoUrl ? (
                    <img src={p.photoUrl} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-base-300 flex items-center justify-center shrink-0 text-xs font-bold text-base-content/50">
                      {(p.firstName?.[0] ?? p.lastName?.[0] ?? "?").toUpperCase()}
                    </div>
                  )}
                  <span className="truncate">{name}</span>
                  {isSelected && <CheckIcon className="w-4 h-4 text-primary ml-auto shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        {targetGuid && (
          <div className="alert alert-warning py-2 text-xs">
            <span>
              <strong>{fromName}</strong> will be deleted. All their matches will be assigned to{" "}
              <strong>{toName}</strong>.
            </span>
          </div>
        )}

        <div className="modal-action mt-0">
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={merging}>Cancel</button>
          <button
            className="btn btn-warning btn-sm"
            disabled={!targetGuid || merging}
            onClick={() => onConfirm(targetGuid)}
          >
            {merging ? <span className="loading loading-spinner loading-xs" /> : "Merge"}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={() => !merging && onClose()} />
    </div>
  );
}

// ── PlayerSearch (for NewMatchFromPlayerModal) ────────────────────────────────

function PlayerSearch({ label, players, selected, onSelect, excludeGuid, onNewPlayer }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = players
    .filter((p) => p.guid !== excludeGuid)
    .filter((p) =>
      `${p.firstName ?? ""} ${p.lastName ?? ""}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    )
    .slice(0, 20);

  const displayName = selected
    ? `${selected.firstName ?? ""} ${selected.lastName ?? ""}`.trim()
    : "";

  return (
    <div>
      <div className="label py-0.5 flex items-center justify-between">
        <span className="label-text text-xs font-semibold">{label}</span>
        {onNewPlayer && (
          <button type="button" className="btn btn-ghost btn-xs p-0.5" title="Create new player" onClick={onNewPlayer}>
            <PlusIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="relative">
        <input
          type="text"
          className="input input-bordered input-sm w-full"
          placeholder="Search player…"
          value={open ? search : displayName}
          onFocus={() => { setSearch(""); setOpen(true); }}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && (
          <ul className="absolute z-50 bg-base-100 border border-base-300 rounded-box shadow-lg w-full max-h-40 overflow-y-auto mt-0.5">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-base-content/50">No players found</li>
            ) : (
              filtered.map((p) => (
                <li
                  key={p.guid}
                  className="px-3 py-1.5 text-sm hover:bg-base-200 cursor-pointer"
                  onMouseDown={() => { onSelect(p); setOpen(false); setSearch(""); }}
                >
                  {p.firstName} {p.lastName}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── QuickNewPlayerPanel (for NewMatchFromPlayerModal) ─────────────────────────

function QuickNewPlayerPanel({ uid, onCreated, onCancel }) {
  const [fields, setFields] = useState({ firstName: "", lastName: "", hand: "0", gender: "Male" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setFields((prev) => ({ ...prev, [k]: v }));
  const canCreate = fields.firstName.trim() || fields.lastName.trim();

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    const player = {
      guid: uuid(),
      uid,
      firstName: fields.firstName.trim(),
      lastName: fields.lastName.trim(),
      hand: parseInt(fields.hand, 10),
      gender: fields.gender,
      dob: 0, height: 0, weight: 0, email: "", country: null, photoUrl: "",
    };
    await savePlayer(player);
    setSaving(false);
    onCreated(player);
  };

  return (
    <div className="bg-base-200 rounded-box p-3 flex flex-col gap-2 border border-primary/30">
      <div className="text-xs font-semibold text-primary uppercase tracking-wide">New Player</div>
      <div className="grid grid-cols-2 gap-2">
        <input className="input input-bordered input-xs" placeholder="First name" value={fields.firstName}
          onChange={(e) => set("firstName", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()} autoFocus />
        <input className="input input-bordered input-xs" placeholder="Last name" value={fields.lastName}
          onChange={(e) => set("lastName", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select className="select select-bordered select-xs" value={fields.hand} onChange={(e) => set("hand", e.target.value)}>
          <option value="0">Right-handed</option>
          <option value="1">Left-handed</option>
          <option value="2">Unknown hand</option>
        </select>
        <select className="select select-bordered select-xs" value={fields.gender} onChange={(e) => set("gender", e.target.value)}>
          <option>Male</option><option>Female</option><option>Unknown</option>
        </select>
      </div>
      <div className="flex gap-2 justify-end">
        <button className="btn btn-ghost btn-xs" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-xs" disabled={!canCreate || saving} onClick={handleCreate}>
          {saving && <span className="loading loading-spinner loading-xs" />}Create
        </button>
      </div>
    </div>
  );
}

// ── NewMatchFromPlayerModal ──────────────────────────────────────────────────

function NewMatchFromPlayerModal({ player1, players, existingTournaments, onSave, onClose, uid = null }) {
  const today = new Date().toISOString().slice(0, 10);
  const [localPlayers, setLocalPlayers] = useState(players);
  const [player2, setPlayer2] = useState(null);
  const [matchDate, setMatchDate] = useState(today);
  const [round, setRound] = useState("R1");
  const [tournamentName, setTournamentName] = useState("");
  const [tournamentOpen, setTournamentOpen] = useState(false);
  const [numSets, setNumSets] = useState(3);
  const [setConfigs, setSetConfigs] = useState(() =>
    Array.from({ length: 3 }, () => ({ ...DEFAULT_SET_CONFIG }))
  );
  // No-Ad scoring: at deuce (40-40) the next point wins the game outright
  // instead of requiring a two-point margin.
  const [noAd, setNoAd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quickNewPlayer, setQuickNewPlayer] = useState(false);
  const [showTournamentForm, setShowTournamentForm] = useState(false);
  const [newTournamentName, setNewTournamentName] = useState("");

  const handlePlayerCreated = (newPlayer) => {
    setLocalPlayers((prev) => prev.find((p) => p.guid === newPlayer.guid) ? prev : [...prev, newPlayer]);
    setPlayer2(newPlayer);
    setQuickNewPlayer(false);
  };

  const updateNumSets = (n) => {
    setNumSets(n);
    setSetConfigs((prev) =>
      Array.from({ length: n }, (_, i) => prev[i] ?? { ...DEFAULT_SET_CONFIG }));
  };

  const updateSetConfig = (idx, field, rawValue) => {
    const value = parseInt(rawValue, 10);
    if (isNaN(value)) return;
    setSetConfigs((prev) => { const next = [...prev]; next[idx] = { ...next[idx], [field]: value }; return next; });
  };

  const tournamentSuggestions = tournamentName.trim()
    ? existingTournaments.filter((t) => t.toLowerCase().includes(tournamentName.toLowerCase()))
    : existingTournaments;

  const canSave = player2 && matchDate;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const dateTs = new Date(matchDate).getTime();
    const roundInfo = ROUNDS.find((r) => r.label === round) ?? { label: round, number: 0 };
    const match = {
      guid: `${player1.guid}_${player2.guid}_${dateTs}`,
      isDoubles: false,
      isPractice: false,
      matchDate: dateTs,
      videoStartTime: 0,
      scoreModeAdvantage: noAd ? 1 : 0,
      scoreModeTiebreak: 0,
      roundName: round,
      temperature: 0,
      videoFile: "",
      importedFrom: "manual",
      coder: "",
      tournament: tournamentName.trim()
        ? {
            name: tournamentName.trim(),
            guid: `tournament_${tournamentName.trim().toLowerCase().replace(/\s+/g, "_")}`,
            startDate: 0,
            enddate: 0,
            gender: "",
            prizeMoney: 0,
            code: "",
            year: new Date(matchDate).getFullYear(),
            country: null,
          }
        : null,
      court: null,
      round: { notes: round, number: roundInfo.number },
      weather: null,
      player1: { ...player1 },
      player1Guid: player1.guid,
      player1FirstName: player1.firstName ?? "",
      player1LastName: player1.lastName ?? "",
      player1CountryCode: player1.country?.code ?? "UNK",
      player2: { ...player2 },
      player2Guid: player2.guid,
      player2FirstName: player2.firstName ?? "",
      player2LastName: player2.lastName ?? "",
      player2CountryCode: player2.country?.code ?? "UNK",
      sets: setConfigs.map((cfg, i) => ({
        number: i + 1,
        currentSkill: 0,
        isTiebreak: false,
        oppositionGameScore: 0,
        oppositionScore: 0,
        playerGameScore: 0,
        playerScore: 0,
        serves: 0,
        servingZone: 0,
        onTopCourt: false,
        user1: "",
        user2: "",
        tiebreakAfterGame: cfg.tiebreakAfterGame,
        tiebreakTo: cfg.tiebreakTo,
        tiebreakMargin: cfg.tiebreakMargin,
        events: [],
      })),
      videos: [],
      uid,
    };
    await onSave(match);
    setSaving(false);
  };

  const p1Name = `${player1.firstName ?? ""} ${player1.lastName ?? ""}`.trim() || "Player 1";

  return (
    <div className="modal modal-open">
      <div className="modal-box w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          <h3 className="font-bold text-lg">New Match</h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose}>
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 flex flex-col gap-4 pr-1">
          {/* Players */}
          <div className="grid grid-cols-2 gap-3">
            {/* Player 1 – locked */}
            <div>
              <div className="label py-0.5">
                <span className="label-text text-xs font-semibold">Player 1</span>
              </div>
              <div className="input input-bordered input-sm w-full bg-base-200 text-base-content/60 cursor-not-allowed flex items-center">
                {p1Name}
              </div>
            </div>
            {/* Player 2 */}
            <PlayerSearch
              label="Player 2"
              players={localPlayers}
              selected={player2}
              onSelect={setPlayer2}
              excludeGuid={player1.guid}
              onNewPlayer={() => setQuickNewPlayer(true)}
            />
          </div>

          {/* Quick new player panel */}
          {quickNewPlayer && (
            <QuickNewPlayerPanel
              uid={uid}
              onCreated={handlePlayerCreated}
              onCancel={() => setQuickNewPlayer(false)}
            />
          )}

          {/* Date + Round */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label py-0.5">
                <span className="label-text text-xs font-semibold">Match Date</span>
              </div>
              <input
                type="date"
                className="input input-bordered input-sm w-full"
                value={matchDate}
                onChange={(e) => setMatchDate(e.target.value)}
              />
            </div>
            <div>
              <div className="label py-0.5">
                <span className="label-text text-xs font-semibold">Round</span>
              </div>
              <select
                className="select select-bordered select-sm w-full"
                value={round}
                onChange={(e) => setRound(e.target.value)}
              >
                {ROUNDS.map((r) => (
                  <option key={r.label} value={r.label}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Tournament */}
          <div className="relative">
            <div className="label py-0.5 flex items-center justify-between">
              <span className="label-text text-xs font-semibold">Tournament (optional)</span>
              <button
                type="button"
                className="btn btn-ghost btn-xs p-0.5"
                title="Add new tournament"
                onClick={() => { setShowTournamentForm((v) => !v); setNewTournamentName(tournamentName); }}
              >
                <PlusIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter or select tournament…"
              value={tournamentName}
              onChange={(e) => { setTournamentName(e.target.value); setTournamentOpen(true); }}
              onFocus={() => setTournamentOpen(true)}
              onBlur={() => setTimeout(() => setTournamentOpen(false), 150)}
            />
            {tournamentOpen && tournamentSuggestions.length > 0 && (
              <ul className="absolute z-50 bg-base-100 border border-base-300 rounded-box shadow-lg w-full max-h-36 overflow-y-auto mt-0.5">
                {tournamentSuggestions.map((t) => (
                  <li
                    key={t}
                    className="px-3 py-1.5 text-sm hover:bg-base-200 cursor-pointer"
                    onMouseDown={() => { setTournamentName(t); setTournamentOpen(false); }}
                  >
                    {t}
                  </li>
                ))}
              </ul>
            )}
            {showTournamentForm && (
              <div className="mt-1 bg-base-200 rounded-box p-2 flex gap-2 items-center border border-primary/30">
                <input
                  type="text"
                  className="input input-bordered input-xs flex-1"
                  placeholder="New tournament name"
                  value={newTournamentName}
                  onChange={(e) => setNewTournamentName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newTournamentName.trim()) {
                      setTournamentName(newTournamentName.trim());
                      setShowTournamentForm(false);
                    }
                  }}
                  autoFocus
                />
                <button
                  className="btn btn-primary btn-xs"
                  disabled={!newTournamentName.trim()}
                  onClick={() => { setTournamentName(newTournamentName.trim()); setShowTournamentForm(false); }}
                >
                  Add
                </button>
                <button className="btn btn-ghost btn-xs" onClick={() => setShowTournamentForm(false)}>
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Number of sets */}
          <div>
            <div className="label py-0.5">
              <span className="label-text text-xs font-semibold">Number of Sets</span>
            </div>
            <div className="flex gap-2">
              {[1, 3, 5].map((n) => (
                <button
                  key={n}
                  className={`btn btn-sm ${numSets === n ? "btn-primary" : "btn-outline"}`}
                  onClick={() => updateNumSets(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Scoring format */}
          <div>
            <div className="label py-0.5">
              <span className="label-text text-xs font-semibold">Match Style</span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={noAd}
                onChange={(e) => setNoAd(e.target.checked)}
              />
              <span className="text-sm">
                No-Ad (deciding point at deuce, no advantage)
              </span>
            </label>
          </div>

          {/* Per-set tiebreak config */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
              Tiebreak Rules per Set
            </div>
            <table className="table table-xs w-full">
              <thead>
                <tr>
                  <th>Set</th>
                  <th>TB after game</th>
                  <th>TB to</th>
                  <th>TB margin</th>
                </tr>
              </thead>
              <tbody>
                {setConfigs.map((cfg, i) => (
                  <tr key={i}>
                    <td className="font-medium">Set {i + 1}</td>
                    <td>
                      <input type="number" className="input input-bordered input-xs w-16" min={1} max={99}
                        value={cfg.tiebreakAfterGame}
                        onChange={(e) => updateSetConfig(i, "tiebreakAfterGame", e.target.value)} />
                    </td>
                    <td>
                      <input type="number" className="input input-bordered input-xs w-16" min={1} max={999}
                        value={cfg.tiebreakTo}
                        onChange={(e) => updateSetConfig(i, "tiebreakTo", e.target.value)} />
                    </td>
                    <td>
                      <input type="number" className="input input-bordered input-xs w-16" min={1} max={10}
                        value={cfg.tiebreakMargin}
                        onChange={(e) => updateSetConfig(i, "tiebreakMargin", e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t border-base-300 shrink-0 mt-4">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving && <span className="loading loading-spinner loading-xs" />}
            Create &amp; Code
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={() => !saving && onClose()} />
    </div>
  );
}

export default Players;
